import type { JSX, ComponentPropsWithoutRef, ReactNode, ReactElement } from 'react';
import { cloneElement, isValidElement } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import type { ColorSchemeTypes } from '../../../components/ColorSchemeWrapper';
import type { ButtonProps, Button } from '../../../components/Button';
import { Ellipsis } from '../../../components/Ellipsis';
import { getColorScheme } from '../../helpers/getColorScheme';
import { lineClampCss } from '../../../css/lineClampCss';

const Message = styled(Ellipsis)`
  line-height: 1.3;
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);
  ${lineClampCss(3)}
  word-break: normal;
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  margin-right: var(--wui-space-03);

  svg {
    align-self: center;
    min-width: 16px;
    margin-right: var(--wui-space-02); /* space between icon & message */
  }
`;

const ActionWrapper = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: auto;
  margin-right: var(--wui-space-03);
`;

const StyledToast = styled.div<{
  $colorScheme: ColorSchemeTypes;
}>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  width: 356px;
  border-radius: var(--wui-border-radius-01);
  background-color: var(--wui-color-bg-surface);
  border: 1px solid var(--wui-color-border);
  color: var(--wui-color-text);
  padding-top: var(--wui-space-02);
  padding-right: 0; /* padding here is on the left/right slots */
  padding-bottom: var(--wui-space-02);
  padding-left: var(--wui-space-03);
  box-shadow: var(--wui-elevation-02);

  svg {
    height: 16px;
    width: 16px;
    flex: 0 0 16px;
  }
`;

export type ToastProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Action can be undefined (default dismiss button), a Button component or false to hide the dismiss button
   */
  action?: ReactElement<typeof Button> | undefined;
  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes | undefined;
  /**
   * Optional `Icon` component to use. Must be an instance of [Icon](/?path=/docs/components-icon--docs)
   */
  icon?: JSX.Element | undefined;
  /**
   * The message displayed in the toast
   */
  message: ReactNode;
};

const Action = ({ actionButton }: { actionButton?: ToastProps['action'] }) => {
  // user-supplied Button force to `soft` variant
  if (isNotNil(actionButton) && isValidElement(actionButton)) {
    // note: the casting nonsense below occurs because actionButton is a ReactElement that wraps
    // a ForwardRefExoticComponent, and TypeScript struggles to confirm its props align with ButtonProps
    return (
      <ActionWrapper>
        {cloneElement(actionButton as unknown as ReactElement<ButtonProps>, {
          variant: 'soft', // force Button variant
          size: 'sm', // force Button size
        })}
      </ActionWrapper>
    );
  }

  // standard non-dismissable Toast
  return null;
};

/**
 * A Toast is a short, non-intrusive notification message that briefly appears on the screen to provide feedback to the user.
 *
 * This page is for screenshots only. For usage, see the [`useToast`](../?path=/docs/hooks-usetoast--docs) hook.
 */
export const Toast = ({
  action,
  message,
  colorScheme = 'inherit',
  icon,
  ...props
}: ToastProps): JSX.Element => {
  return (
    <StyledToast
      {...props}
      $colorScheme={colorScheme}
    >
      <MessageWrapper>
        {isNotNil(icon) ? icon : null}
        <Message lines={3}>{message}</Message>
      </MessageWrapper>
      <Action actionButton={action} />
    </StyledToast>
  );
};

Toast.displayName = 'Toast';
